// FullCalendar converts dates to a named time zone (Europe/Paris…) only through a plugin. Without
// one it places events at their UTC hour (a 09:00 Paris appointment sits on the 07:00 slot) while
// the now line, selections and modals use the real hour. This plugin computes offsets with Intl,
// so no moment-timezone / luxon dependency.
const formatters = {}

function formatter (timeZone) {
  formatters[timeZone] ||= new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  })

  return formatters[timeZone]
}

// Minutes to add to UTC to get the wall clock of `timeZone` at the instant `ms`.
export function timeZoneOffsetAt (timeZone, ms) {
  const instant = Math.floor(ms / 1000) * 1000
  const parts = Object.fromEntries(
    formatter(timeZone).formatToParts(new Date(instant)).map(({ type, value }) => [type, Number(value)])
  )
  const wallClock = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour % 24, parts.minute, parts.second)

  return Math.round((wallClock - instant) / 60000)
}

// FullCalendar date arrays: [year, month (0-based), day, hour, minute, second, ms] on the wall clock.
export function timeZoneOffsetForArray (timeZone, array) {
  const wallClock = Date.UTC(...array)
  const guess = timeZoneOffsetAt(timeZone, wallClock)

  return timeZoneOffsetAt(timeZone, wallClock - guess * 60000)
}

export function timeZoneArrayForTimestamp (timeZone, ms) {
  const date = new Date(ms + timeZoneOffsetAt(timeZone, ms) * 60000)

  return [
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
    date.getUTCMilliseconds()
  ]
}

// Built from the lazily loaded FullCalendar modules (load_full_calendar.js).
export function namedTimeZonePlugin ({ createPlugin, NamedTimeZoneImpl }) {
  class IntlNamedTimeZone extends NamedTimeZoneImpl {
    offsetForArray (array) {
      return timeZoneOffsetForArray(this.timeZoneName, array)
    }

    timestampToArray (ms) {
      return timeZoneArrayForTimestamp(this.timeZoneName, ms)
    }
  }

  return createPlugin({ name: "intl-named-time-zone", namedTimeZonedImpl: IntlNamedTimeZone })
}
